'use client';

import { lazy, useCallback, useEffect, useState } from 'react';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import LoadingScreen from '@/components/LoadingScreen';
import CustomScrollbar from '@/components/CustomScrollbar';
import ScrollToTop from '@/components/ScrollToTop';
import DeferredSection from '@/components/DeferredSection';
import ScrollReveals from '@/components/ScrollReveals';
import SectionDivider from '@/components/SectionDivider';
import useAdaptiveEffects from '@/hooks/useAdaptiveEffects';

const loadVideos = () => import('@/components/Videos');
const loadGear = () => import('@/components/Gear');
const loadAbout = () => import('@/components/About');

const Videos = lazy(loadVideos);
const Gear = lazy(loadGear);
const About = lazy(loadAbout);

// SmoothScroll removed to fix console warnings

export default function Home() {
    const [isLoading, setIsLoading] = useState(true);
    const [isNavLogoVisible, setIsNavLogoVisible] = useState(false);
    const useLiteEffects = useAdaptiveEffects();

    // Removed document-level anchor override to prevent conflicts with explicit handlers

    const handleLoadingComplete = useCallback(() => {
        setIsNavLogoVisible(true);
        setIsLoading(false);
    }, []);

    useEffect(() => {
        // Prepare the first section promptly, then leave lower-page code until
        // the browser is idle so initial interaction is not competing with it.
        void loadVideos();

        const preloadLowerSections = () => {
            void Promise.allSettled([loadGear(), loadAbout()]);
        };

        const idleWindow = window as Window & {
            requestIdleCallback?: (callback: IdleRequestCallback, options?: IdleRequestOptions) => number;
            cancelIdleCallback?: (handle: number) => void;
        };
        if (idleWindow.requestIdleCallback) {
            const idleId = idleWindow.requestIdleCallback(preloadLowerSections, { timeout: 2500 });
            return () => idleWindow.cancelIdleCallback?.(idleId);
        }

        const timerId = window.setTimeout(preloadLowerSections, 1200);
        return () => window.clearTimeout(timerId);
    }, []);

    useEffect(() => {
        document.documentElement.classList.toggle('effects-lite', useLiteEffects);

        return () => document.documentElement.classList.remove('effects-lite');
    }, [useLiteEffects]);

    return (
        <>
            <ScrollToTop />
            <ScrollReveals />
            {isLoading && (
                <LoadingScreen
                    onDocked={() => setIsNavLogoVisible(true)}
                    onComplete={handleLoadingComplete}
                />
            )}
            <CustomScrollbar introActive={isLoading} />

            <main className="site-shell relative">
                {/* Top Navigation Bar */}
                <Navigation introActive={!isNavLogoVisible} />

                {/* Navigation dots */}
                <nav className="fixed right-6 top-1/2 z-40 hidden -translate-y-1/2 xl:block" aria-label="Section shortcuts">
                    <ul className="nav-shell rounded-full p-1.5">
                        {['hero', 'media', 'gear', 'about'].map((section) => (
                            <li key={section}>
                                <a
                                    href={`#${section}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (section === 'hero') {
                                            // For hero section, scroll to top
                                            window.scrollTo({
                                                top: 0,
                                                behavior: 'smooth'
                                            });
                                            return;
                                        }

                                        const element = document.getElementById(section);
                                        if (element) {
                                            // Get the navbar height to offset the scroll position
                                            const navbarHeight = 64; // h-16 = 64px
                                            const rect = element.getBoundingClientRect();
                                            const scrollTop = window.pageYOffset;
                                            const elementTop = rect.top + scrollTop;

                                            // Add extra offset to ensure sections are visible
                                            const offsetPosition = elementTop - navbarHeight + 100;

                                            window.scrollTo({
                                                top: offsetPosition,
                                                behavior: 'smooth'
                                            });
                                        }
                                    }}
                                    className="group flex h-6 w-6 items-center justify-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
                                    aria-label={`Go to ${section} section`}
                                >
                                    <span className="h-2 w-2 rounded-full border border-white/20 bg-white/10 transition-all duration-200 group-hover:border-cyan-300/60 group-hover:bg-cyan-300 group-hover:shadow-[0_0_10px_#38dcff]" />
                                </a>
                            </li>
                        ))}
                    </ul>
                </nav>

                {/* Sections */}
                <Hero />
                <SectionDivider topAligned heroFade />
                <div className="relative z-10 min-h-screen bg-[var(--bg-primary)]">
                    <DeferredSection id="media" label="media" prepareOnMount>
                        <Videos />
                    </DeferredSection>
                </div>
                <SectionDivider />
                <DeferredSection
                    id="gear"
                    label="gear"
                    rootMargin="0px 0px 900px 0px"
                    placeholderClassName="deferred-placeholder-gear"
                >
                    <Gear />
                </DeferredSection>
                <DeferredSection
                    id="about"
                    label="about section"
                    rootMargin="0px 0px 1200px 0px"
                    placeholderClassName="deferred-placeholder-about"
                >
                    <About useLiteEffects={useLiteEffects} />
                </DeferredSection>
            </main>
        </>
    );
}
